import { useContext, useEffect } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAgencySelector from '@hooks/useAgencySelector'
import useAssignCampaignTeamOrAgents from '@hooks/useAssignCampaignTeamOrAgents'
import { useCampaignForm } from '@hooks/useCampaign'

import AgentOrTeamSearch from '@components/AgentOrTeamSearch'
import EntitySelectBox from '@components/EntitySelectBox'
import SelectedAgentsAndTeam from '@components/SelectedAgentsAndTeam'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import AddressForm from './components/AddressForm'

const MODAL_KEY = 'CreateCampaignModal'

const customRequiredFields = [
  { key: 'state_name' },
  { key: 'post_code' },
  { key: 'country_code' },
]

type ModalCallbacks = {
  closeModal: () => void,
  createCampaign: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateCampaignModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCampaign } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaign: initCampaign } = modalPayload

  const campaignFormPayload = useCampaignForm(initCampaign, { validateOn: 'change', customRequiredFields })

  const assignCampaignTeamOrAgentsPayload = useAssignCampaignTeamOrAgents({
    campaign: initCampaign,
    campaignFormPayload,
  })
  const {
    callbacks: {
      selectAgentOrTeam,
    },
    campaignPayload,
    hasAssignedAgents,
    hasAssignedTeam,
  } = assignCampaignTeamOrAgentsPayload

  const {
    callbacks: { createCampaign: createFn },
    creating,
  } = campaignPayload

  const {
    entityState,
    entityState: {
      agency_id,
    },
    handlers,
    saveEnabled,
  } = campaignFormPayload

  const { hasMultipleAgencies } = useAgencySelector()

  const createCampaignPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: true,
    toastText: 'Campaign Created',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Start" titleSecondLine="Property Marketing" />

      <SidebarModal.Content>
        <Form>
          {hasMultipleAgencies && (
            <Form.Field label="* Agency">
              <EntitySelectBox
                data-value-type="number"
                entityKey="agencies"
                // @ts-expect-error TS2322
                name="agency_id"
                value={agency_id}
                {...handlers}
              />
            </Form.Field>
          )}

          <Form.Field
            label="* Assign Team & Agents"
            marginTop={hasMultipleAgencies ? 'large' : ''}
          >
            <AgentOrTeamSearch
              callbacks={{ selectAgentOrTeam }}
              selectedAgentIds={entityState.agent_ids || []}
              selectedTeamId={entityState.team_id}
            />
          </Form.Field>

          <SelectedAgentsAndTeam
            assignCampaignTeamOrAgentsPayload={assignCampaignTeamOrAgentsPayload}
            compact
          />

          {(hasAssignedAgents || hasAssignedTeam) && !!agency_id && (
            <AddressForm campaignFormPayload={campaignFormPayload} />
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          // @ts-expect-error TS2345
          onClick={() => createCampaign(createCampaignPayload)}
          size="large"
        >
          {saveEnabled ? 'Create Campaign' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateCampaignModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
